import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Download, RefreshCw, Users, Mail, Newspaper, DollarSign, Target, ShoppingBag, HandHeart } from "lucide-react";
import { api, authHeaders } from "@/lib/api";

const TABS = [
  { key: "volunteers", label: "Volunteer Signups", icon: Users, tid: "admin-tab-volunteers" },
  { key: "contacts", label: "Contact Inquiries", icon: Mail, tid: "admin-tab-contacts" },
  { key: "newsletters", label: "Newsletter Subscribers", icon: Newspaper, tid: "admin-tab-newsletters" },
  { key: "pledges", label: "Donation Pledges", icon: DollarSign, tid: "admin-tab-pledges" },
  { key: "donations", label: "Donations", icon: HandHeart, tid: "admin-tab-donations" },
  { key: "orders", label: "Store Orders", icon: ShoppingBag, tid: "admin-tab-orders" },
  { key: "goals", label: "Impact Goals", icon: Target, tid: "admin-tab-goals" },
];

const COLS = {
  volunteers: ["name", "email", "phone", "area", "availability", "skills", "message", "created_at"],
  contacts: ["name", "email", "subject", "message", "created_at"],
  newsletters: ["email", "role", "created_at"],
  pledges: ["email", "amount", "created_at"],
  donations: ["email", "amount", "currency", "payment_status", "created_at"],
  orders: ["email", "items_summary", "amount", "currency", "payment_status", "created_at"],
};

const toCSV = (rows) => {
  if (!rows.length) return "";
  const cols = COLS_KEYS(rows);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};
const COLS_KEYS = (rows) => Object.keys(rows[0]);

const downloadCSV = (rows, name) => {
  const blob = new Blob([toCSV(rows)], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("game_admin_token", data.access_token);
      toast.success(`Welcome back, ${data.name}.`);
      onLogin(data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080C] bg-grid flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md border border-[#242436] bg-[#111118] p-10 shadow-[0_4px_40px_rgba(0,0,0,0.6)]">
        <div className="font-heading font-black text-2xl text-[#F4F4F6] flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 bg-[#8B5CF6] inline-block" />
          GAME<span className="text-[#8B5CF6]">.</span>
        </div>
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800] mt-2">Admin Access</div>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            data-testid="admin-email-input"
            className="w-full bg-[#161622] border border-[#242436] focus:border-[#8B5CF6] outline-none text-[#F4F4F6] text-sm px-4 py-3.5 placeholder:text-[#62627A] transition-colors duration-200"
          />
          <input
            required
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-password-input"
            className="w-full bg-[#161622] border border-[#242436] focus:border-[#8B5CF6] outline-none text-[#F4F4F6] text-sm px-4 py-3.5 placeholder:text-[#62627A] transition-colors duration-200"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full bg-[#8B5CF6] text-black font-display font-extrabold uppercase tracking-wider text-base px-8 py-4 hover:bg-[#FFB800] active:scale-[0.98] disabled:opacity-50 transition-all duration-200"
          >
            {busy ? "Checking..." : "Enter Dashboard"}
          </button>
        </form>
        <Link to="/" data-testid="admin-back-home-link" className="block mt-6 font-mono text-xs uppercase tracking-[0.2em] text-[#62627A] hover:text-[#F4F4F6] transition-colors">
          ← Back to site
        </Link>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("game_admin_token"));
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("volunteers");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/submissions", authHeaders());
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("game_admin_token");
        setToken(null);
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Could not load submissions.");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleGoal = async (g) => {
    try {
      await api.patch(`/admin/goals/${g.slug}`, { status: g.status === "completed" ? "planned" : "completed" }, authHeaders());
      toast.success(g.status === "completed" ? "Goal reopened." : "Goal marked complete.");
      load();
    } catch (err) {
      toast.error("Could not update goal.");
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) return <Login onLogin={setToken} />;

  const rows = data?.[tab] || [];
  const cols = COLS[tab];

  return (
    <div className="min-h-screen bg-[#08080C] text-[#F4F4F6]" data-testid="admin-dashboard">
      <header className="border-b border-[#242436] bg-[#0C0C12] px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="font-heading font-black text-xl flex items-center gap-1.5">
            <span className="w-2 h-2 bg-[#8B5CF6] inline-block" />
            GAME<span className="text-[#8B5CF6]">.</span>
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Submissions HQ</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            data-testid="admin-refresh-button"
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] border border-[#3D3D58] px-4 py-2 hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={() => downloadCSV(rows, `game-${tab}`)}
            data-testid="admin-export-csv-button"
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] border border-[#3D3D58] px-4 py-2 hover:border-[#FFB800] hover:text-[#FFB800] transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
          <button
            onClick={() => { localStorage.removeItem("game_admin_token"); setToken(null); }}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] bg-[#8B5CF6] text-black px-4 py-2 hover:bg-[#FFB800] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>
      </header>

      <div className="px-4 sm:px-8 py-6 flex flex-wrap gap-3 border-b border-[#242436]">
        {TABS.map(({ key, label, icon: Icon, tid }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={tid}
            className={`flex items-center gap-2 font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 border transition-all duration-200 ${
              tab === key
                ? "bg-[#8B5CF6] text-black border-[#8B5CF6]"
                : "border-[#3D3D58] text-[#A1A1B3] hover:border-[#8B5CF6] hover:text-[#F4F4F6]"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {data && (
              <span className={`font-mono text-[10px] px-1.5 py-0.5 ${tab === key ? "bg-black/20" : "bg-[#242436]"}`}>
                {data[key].length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="px-4 sm:px-8 py-8 overflow-x-auto" data-testid="admin-submissions-table">
        {tab === "goals" ? (
          <div className="space-y-3 max-w-4xl">
            {(data?.goals || []).map((g) => (
              <div key={g.slug} className="flex flex-col sm:flex-row sm:items-center gap-4 border border-[#242436] bg-[#111118] p-5" data-testid={`admin-goal-${g.slug}`}>
                <div className="flex-1">
                  <p className="text-sm text-[#F4F4F6] leading-relaxed">{g.title}</p>
                  {g.completed_at && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#62627A] mt-1">
                      Completed {new Date(g.completed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <span
                  className={`shrink-0 font-mono text-[10px] font-bold uppercase tracking-widest px-2 py-1 w-fit ${
                    g.status === "completed" ? "bg-[#FFB800]/15 text-[#FFB800]" : "bg-[#8B5CF6]/15 text-[#C4B5FD]"
                  }`}
                  data-testid={`goal-status-${g.slug}`}
                >
                  {g.status}
                </span>
                <button
                  onClick={() => toggleGoal(g)}
                  data-testid={`goal-toggle-${g.slug}`}
                  className={`shrink-0 font-display font-bold uppercase tracking-wider text-xs px-4 py-2 transition-colors ${
                    g.status === "completed"
                      ? "border border-[#3D3D58] text-[#A1A1B3] hover:border-[#8B5CF6] hover:text-[#F4F4F6]"
                      : "bg-[#FFB800] text-black hover:bg-[#8B5CF6]"
                  }`}
                >
                  {g.status === "completed" ? "Reopen" : "Mark Complete"}
                </button>
              </div>
            ))}
          </div>
        ) : loading && !data ? (
          <p className="font-mono text-sm text-[#62627A]">Loading submissions...</p>
        ) : rows.length === 0 ? (
          <p className="font-mono text-sm text-[#62627A]" data-testid="admin-empty-state">No submissions yet. They'll appear here as they come in.</p>
        ) : (
          <table className="w-full text-left border border-[#242436] min-w-[720px]">
            <thead>
              <tr className="bg-[#111118]">
                {cols.map((c) => (
                  <th key={c} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB800] px-4 py-3 border-b border-[#242436]">
                    {c.replace("_", " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="odd:bg-[#0C0C12] even:bg-[#111118] hover:bg-[#161622] transition-colors">
                  {cols.map((c) => (
                    <td key={c} className="text-xs text-[#A1A1B3] px-4 py-3 border-b border-[#242436] max-w-[260px] truncate" title={r[c]}>
                      {c === "created_at" ? fmt(r[c]) : r[c] || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
